import jsPDF from 'jspdf'
import 'jspdf-autotable'
import {
  computeCustomIngredientLineCost,
  computeIngredientLineCost,
  extractAndCalculateSize,
  findVenueProductForIngredient,
  resolveIngredientUnitPrice,
} from 'src/utils/priceFunctions'
import {
  computeRecipeIngredientLineSum,
  recipeEconomicsAreStale,
} from 'src/utils/recipeEconomicsStale'
import { formatGpDisplay } from 'src/utils/recipeGp'
import { calculateSellingPriceInclVat } from 'src/utils/vatCalculations'

/**
 * Recipe pages carry a running header at y=10 and a footer rule at
 * pageHeight-20, so body content has to stay clear of both.
 */
const CONTENT_TOP = 26
const CONTENT_BOTTOM_RESERVE = 26

const formatMoney = value => {
  const num = Number(value)
  return Number.isFinite(num) ? num.toFixed(2) : '0.00'
}

class PDFGenerator {
  /** Title lookup + economics context, mirroring what RecipeDetails builds. */
  buildTransformContext(allRecipes = [], venueProductsCatalog = []) {
    const recipes = allRecipes || []
    return {
      recipeTitleById: new Map(
        recipes.map(recipe => [String(recipe?._id), recipe?.title]),
      ),
      recipeById: new Map(recipes.map(recipe => [recipe?._id, recipe])),
      venueProductsCatalog,
    }
  }

  transformRecipe(
    fullRecipe,
    {
      recipeTitleById = new Map(),
      recipeById = new Map(),
      venueProductsCatalog = [],
    } = {},
  ) {
    const recipeIngredients = (fullRecipe.recipeIngredients || []).map(ri => {
      const portions = Number(ri?.portions || 1)
      const recipeId = ri?.recipeId || ri?.recipe?._id
      // Same lookup key as computeRecipeIngredientLineSum, so the table row and
      // the Cost Information header are always derived from one source.
      const baseRecipe = recipeById.get(ri?.recipeId || ri?.recipe)
      const titleFromMap = recipeId
        ? recipeTitleById.get(String(recipeId))
        : null
      const recipeName =
        ri?.recipe?.title ||
        titleFromMap ||
        ri?.baseRecipe?.title ||
        ri?.recipeName ||
        ri?.title ||
        (recipeId ? `Recipe ${String(recipeId).slice(-6)}` : null) ||
        'Recipe ingredient'
      // A linked sub-recipe is priced from the live recipe, never the snapshot
      // stored on the parent: that snapshot goes stale the moment the
      // sub-recipe is repriced. Mirrors Recipe Details.
      const isLinked = ri?.isLinked !== false
      const costPerPortion = Number(
        isLinked && baseRecipe?.costPerPortion != null
          ? baseRecipe.costPerPortion
          : ri?.costPerPortion ?? baseRecipe?.costPerPortion ?? 0,
      )
      const totalCost = costPerPortion * portions

      return {
        name: `Recipe Ingredient (${recipeName})`,
        sku: '',
        quantity: portions,
        uom: 'portion(s)',
        packSize: '',
        price: totalCost,
      }
    })
    // Catalogue ingredients: priced via the shared helpers so the PDF matches
    // Recipe Details (pence/pounds handling, pack pro-rata, unit conversion,
    // Brakes / venue product override).
    const ingredients = (fullRecipe.ingredients || []).map(ing => {
      const selectedSize =
        ing?.size ||
        extractAndCalculateSize(ing?.product?.size || '').numbers?.[0] ||
        0
      const venueProduct = findVenueProductForIngredient(
        ing,
        venueProductsCatalog,
      )
      const unitPrice = resolveIngredientUnitPrice({
        ingredient: ing,
        venueProduct,
        product: ing?.product,
      })
      const { lineTotal } = computeIngredientLineCost({
        productSize: ing?.product?.size || '',
        unitPrice,
        selectedSize,
        UoM: ing?.UoM,
        conversion: ing?.conversion,
      })

      return {
        name: ing?.product?.title,
        sku: ing?.product?.sku || ing?.sku || '',
        quantity: ing?.size,
        uom: ing?.UoM || ing?.product?.UoM,
        packSize: ing?.product?.size || '',
        price: lineTotal || 0,
      }
    })

    // Custom products store price differently, so they need their own helper.
    const customIngredients = (fullRecipe.customIngredients || []).map(ing => ({
      name: ing?.product?.title || ing?.title,
      sku: ing?.product?.sku || ing?.sku || '',
      quantity: ing?.size,
      uom: ing?.UoM || ing?.product?.UoM,
      packSize: ing?.product?.size || '',
      price: computeCustomIngredientLineCost(ing).lineTotal || 0,
    }))

    const sellingPriceExVat = Number(fullRecipe.sellingPrice)
    const sellingPriceIncVat =
      calculateSellingPriceInclVat(
        fullRecipe.sellingPrice,
        fullRecipe.hasVat,
      ) || sellingPriceExVat

    // Prefer the persisted header figures, falling back to the recomputed line
    // sum, so the PDF can never disagree with Recipe Details.
    const portions = Number(fullRecipe.portions) || 1
    const lineSum = computeRecipeIngredientLineSum(fullRecipe, {
      recipeById,
      venueProductsCatalog,
    })
    const persistedCostPerRecipe = Number(fullRecipe.costPerRecipe)
    const persistedCostPerPortion = Number(fullRecipe.costPerPortion)

    return {
      name: fullRecipe.title,
      image: fullRecipe.image,
      category: fullRecipe.category?.name || '',
      costPerRecipe: Number.isFinite(persistedCostPerRecipe)
        ? persistedCostPerRecipe
        : lineSum,
      portions,
      costPerPortion: Number.isFinite(persistedCostPerPortion)
        ? persistedCostPerPortion
        : Math.round((lineSum / portions) * 100) / 100,
      lineSum,
      economicsAreStale: recipeEconomicsAreStale(
        persistedCostPerRecipe,
        lineSum,
      ),
      sellingPriceExVat,
      sellingPriceIncVat,
      hasVat: !!fullRecipe.hasVat,
      grossProfit: formatGpDisplay(fullRecipe.grossProfit).valueText,
      hideGrossProfit: fullRecipe?.monitored === false,
      ingredients: [...ingredients, ...customIngredients, ...recipeIngredients],
      allergens: Array.isArray(fullRecipe?.allergens)
        ? fullRecipe.allergens
        : [],
      allergenTraces: Array.isArray(fullRecipe?.allergenTraces)
        ? fullRecipe.allergenTraces
        : [],
      notes: {
        text:
          typeof fullRecipe.notes?.text === 'string'
            ? fullRecipe.notes.text
            : '',
        images: Array.isArray(fullRecipe.notes?.images)
          ? fullRecipe.notes.images
          : [],
      },
      steps: this.getRecipeStepList(fullRecipe),
    }
  }

  getRecipeId(value) {
    if (!value) return null
    if (typeof value === 'string' || typeof value === 'number') {
      return String(value)
    }
    if (typeof value === 'object') {
      return String(value._id || value.id || '')
    }
    return null
  }

  getDirectStackedRecipeIds(recipeRaw) {
    const stacked = Array.isArray(recipeRaw?.recipeIngredients)
      ? recipeRaw.recipeIngredients
      : []
    const seen = new Set()
    const orderedIds = []

    stacked.forEach(item => {
      const recipeId = this.getRecipeId(item?.recipeId || item?.recipe || item)
      if (!recipeId || seen.has(recipeId)) return
      seen.add(recipeId)
      orderedIds.push(recipeId)
    })

    return orderedIds
  }

  buildRecipeByIdMap(allRecipes = []) {
    const map = new Map()
    ;(allRecipes || []).forEach(recipe => {
      const recipeId = this.getRecipeId(recipe?._id || recipe?.id)
      if (!recipeId || map.has(recipeId)) return
      map.set(recipeId, recipe)
    })
    return map
  }

  normalizeAppendixIngredients(stackedRecipe = {}) {
    const omsIngredients = (stackedRecipe.ingredients || []).map(ing => ({
      name:
        ing?.product?.title || ing?.title || ing?.name || 'Unknown ingredient',
      quantity: ing?.size ?? ing?.quantity ?? 0,
      uom: ing?.UoM || ing?.uom || ing?.product?.UoM || '',
    }))

    const customIngredients = (stackedRecipe.customIngredients || []).map(
      ing => ({
        name:
          ing?.product?.title || ing?.title || ing?.name || 'Custom ingredient',
        quantity: ing?.size ?? ing?.quantity ?? 0,
        uom: ing?.UoM || ing?.uom || ing?.product?.UoM || '',
      }),
    )

    return [...omsIngredients, ...customIngredients]
  }

  /**
   * Chefs often type their own "1." into a step; the renderer adds numbering,
   * so strip any existing prefix instead of printing "1. 1. Heat the pan".
   */
  stripStepNumberPrefix(step) {
    return String(step ?? '')
      .replace(/^\s*\d+\s*[.)]\s*/, '')
      .trim()
  }

  /** Clean, numbered-ready step list from either an array or newline string. */
  getRecipeStepList(recipe = {}) {
    return this.normalizeAppendixSteps(recipe)
      .map(step => this.stripStepNumberPrefix(step))
      .filter(Boolean)
  }

  normalizeAppendixSteps(stackedRecipe = {}) {
    if (Array.isArray(stackedRecipe.steps)) {
      return stackedRecipe.steps.filter(Boolean)
    }

    if (typeof stackedRecipe.steps === 'string') {
      return stackedRecipe.steps
        .split('\n')
        .map(step => step.trim())
        .filter(Boolean)
    }

    return []
  }

  normalizeAppendixAllergens(stackedRecipe = {}) {
    const allergens = Array.isArray(stackedRecipe.allergens)
      ? stackedRecipe.allergens
      : []
    const allergenTraces = Array.isArray(stackedRecipe.allergenTraces)
      ? stackedRecipe.allergenTraces
      : []

    return { allergens, allergenTraces }
  }

  buildAppendixRecipeModel(stackedRecipe = {}) {
    const allergenData = this.normalizeAppendixAllergens(stackedRecipe)
    return {
      id: this.getRecipeId(stackedRecipe?._id || stackedRecipe?.id),
      name: stackedRecipe?.title || stackedRecipe?.name || 'Unnamed recipe',
      ingredients: this.normalizeAppendixIngredients(stackedRecipe),
      steps: this.getRecipeStepList(stackedRecipe),
      ...allergenData,
    }
  }

  getAppendixSubRecipes(recipeRaw, allRecipes = []) {
    const directStackedIds = this.getDirectStackedRecipeIds(recipeRaw)
    if (!directStackedIds.length) return []

    const recipeById = this.buildRecipeByIdMap(allRecipes)
    return directStackedIds
      .map(recipeId => recipeById.get(recipeId))
      .filter(Boolean)
      .map(stackedRecipe => this.buildAppendixRecipeModel(stackedRecipe))
  }

  ensureVerticalSpace(doc, yPosition, requiredHeight = 20) {
    const pageHeight = doc.internal.pageSize.height
    if (yPosition + requiredHeight > pageHeight - CONTENT_BOTTOM_RESERVE) {
      doc.addPage()
      return CONTENT_TOP
    }
    return yPosition
  }

  /**
   * Draw text wrapped to the printable width, returning the y position after
   * the block. Callers set colour/font style; this only owns size and layout.
   */
  drawWrappedText(
    doc,
    text,
    yPosition,
    { fontSize, lineHeight, gap = 0, x = 20 },
  ) {
    const maxWidth = doc.internal.pageSize.width - x - 20
    doc.setFontSize(fontSize)
    const lines = doc.splitTextToSize(String(text ?? ''), maxWidth)
    doc.text(lines, x, yPosition)
    return yPosition + lines.length * lineHeight + gap
  }

  /**
   * Ingredient table rows. Partial ingredient data must degrade to a blank
   * cell rather than throwing and aborting the whole download.
   */
  buildIngredientRows(ingredients = []) {
    return (ingredients || []).map(ingredient => {
      const quantity = ingredient?.quantity

      return [
        ingredient?.sku || '',
        ingredient?.name || 'Unnamed ingredient',
        quantity === null || quantity === undefined || quantity === ''
          ? ''
          : String(quantity),
        ingredient?.uom || ingredient?.packSize || '',
        `£${formatMoney(ingredient?.price)}`,
      ]
    })
  }

  buildIngredientTotalRow(ingredients = []) {
    const total = (ingredients || []).reduce((sum, ingredient) => {
      const price = Number(ingredient?.price)
      return Number.isFinite(price) ? sum + price : sum
    }, 0)

    return ['', '', '', 'Total', `£${formatMoney(total)}`]
  }

  buildRecipeFileName(recipeName) {
    const slug = String(recipeName || '')
      .replace(/[^a-z0-9]/gi, '_')
      .toLowerCase()
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
      .slice(0, 100)

    return `${slug || 'recipe'}_recipe.pdf`
  }

  /**
   * Draw already-wrapped lines, breaking onto a new page when the block is
   * taller than the space left. Long notes/methods must not run off the page.
   */
  drawPaginatedLines(doc, lines = [], yPosition, lineHeight, x = 20) {
    lines.forEach(line => {
      yPosition = this.ensureVerticalSpace(doc, yPosition, lineHeight)
      doc.text(line, x, yPosition)
      yPosition += lineHeight
    })

    return yPosition
  }

  /**
   * Numbered list with a hanging indent, so wrapped lines sit under the text
   * rather than the number and can't be mistaken for a new step.
   */
  drawNumberedList(
    doc,
    items = [],
    yPosition,
    { lineHeight = 5, gap = 3, x = 20 } = {},
  ) {
    if (items.length === 0) return yPosition

    // Align every step to the widest number so the text column is straight.
    const labelWidth = Math.max(
      ...items.map((_, index) => doc.getTextWidth(`${index + 1}. `)),
    )
    const textX = x + labelWidth
    const maxWidth = doc.internal.pageSize.width - textX - 20

    items.forEach((item, index) => {
      const lines = doc.splitTextToSize(String(item ?? ''), maxWidth)

      lines.forEach((line, lineIndex) => {
        yPosition = this.ensureVerticalSpace(doc, yPosition, lineHeight)
        if (lineIndex === 0) {
          doc.text(`${index + 1}.`, x, yPosition)
        }
        doc.text(line, textX, yPosition)
        yPosition += lineHeight
      })

      yPosition += gap
    })

    return yPosition
  }

  /** Single-line fit for running headers, where wrapping isn't an option. */
  truncateToWidth(doc, text, maxWidth) {
    const value = String(text ?? '')
    if (maxWidth <= 0) return ''
    if (doc.getTextWidth(value) <= maxWidth) return value

    const ellipsis = '...'
    let truncated = value
    while (
      truncated.length > 0 &&
      doc.getTextWidth(`${truncated}${ellipsis}`) > maxWidth
    ) {
      truncated = truncated.slice(0, -1)
    }

    return truncated ? `${truncated.trimEnd()}${ellipsis}` : ''
  }

  renderAppendixSectionTitle(doc, yPosition) {
    const nextY = this.ensureVerticalSpace(doc, yPosition, 18)
    doc.setFontSize(18)
    doc.setTextColor(0, 0, 0)
    doc.text('Appendix: Sub-Recipes', 20, nextY)
    return nextY + 10
  }

  renderAppendixAllergenBlock(doc, yPosition, appendixRecipe) {
    let nextY = yPosition
    const hasAllergens = Array.isArray(appendixRecipe?.allergens)
      ? appendixRecipe.allergens.length > 0
      : false
    const hasAllergenTraces = Array.isArray(appendixRecipe?.allergenTraces)
      ? appendixRecipe.allergenTraces.length > 0
      : false

    if (!hasAllergens && !hasAllergenTraces) return nextY

    nextY = this.ensureVerticalSpace(doc, nextY, 16)
    doc.setFontSize(11)
    doc.setTextColor(0, 0, 0)

    if (hasAllergens) {
      const allergenText = `Allergens: ${appendixRecipe.allergens.join(', ')}`
      const splitAllergenText = doc.splitTextToSize(
        allergenText,
        doc.internal.pageSize.width - 40,
      )
      const neededHeight = splitAllergenText.length * 5 + 4
      nextY = this.ensureVerticalSpace(doc, nextY, neededHeight)
      doc.text(splitAllergenText, 20, nextY)
      nextY += splitAllergenText.length * 5 + 2
    }

    if (hasAllergenTraces) {
      const tracesText = `May contain: ${appendixRecipe.allergenTraces.join(
        ', ',
      )}`
      const splitTracesText = doc.splitTextToSize(
        tracesText,
        doc.internal.pageSize.width - 40,
      )
      const neededHeight = splitTracesText.length * 5 + 4
      nextY = this.ensureVerticalSpace(doc, nextY, neededHeight)
      doc.text(splitTracesText, 20, nextY)
      nextY += splitTracesText.length * 5 + 2
    }

    return nextY
  }

  renderSingleAppendixRecipe(doc, yPosition, appendixRecipe) {
    let nextY = this.ensureVerticalSpace(doc, yPosition, 24)

    doc.setTextColor(0, 0, 0)
    nextY = this.drawWrappedText(
      doc,
      appendixRecipe?.name || 'Unnamed recipe',
      nextY,
      { fontSize: 14, lineHeight: 7, gap: 1 },
    )

    const ingredientRows = (appendixRecipe?.ingredients || []).map(
      ingredient => [
        ingredient?.name || 'Unknown ingredient',
        String(ingredient?.quantity ?? ''),
        ingredient?.uom || '',
      ],
    )

    if (ingredientRows.length > 0) {
      nextY = this.ensureVerticalSpace(doc, nextY, 14)
      doc.setFontSize(11)
      doc.text('Ingredients', 20, nextY)
      nextY += 4

      doc.autoTable({
        startY: nextY,
        head: [['Ingredient', 'Qty', 'UOM']],
        body: ingredientRows,
        theme: 'grid',
        headStyles: { fillColor: '#b5d922' },
        margin: { left: 20, right: 20 },
        styles: { fontSize: 10 },
      })
      nextY = doc.lastAutoTable.finalY + 6
    }

    // Same numbered / hanging-indent layout as the main Steps block.
    const steps = this.getRecipeStepList(appendixRecipe)
    if (steps.length > 0) {
      nextY = this.ensureVerticalSpace(doc, nextY, 14)
      doc.setFontSize(11)
      doc.setTextColor(0, 0, 0)
      doc.text('Method', 20, nextY)
      nextY += 5

      nextY = this.drawNumberedList(doc, steps, nextY, { gap: 2 }) + 2
    }

    nextY = this.renderAppendixAllergenBlock(doc, nextY, appendixRecipe)
    nextY += 6

    const pageWidth = doc.internal.pageSize.width
    const separatorY = this.ensureVerticalSpace(doc, nextY, 6)
    doc.setDrawColor(220, 220, 220)
    doc.setLineWidth(0.2)
    doc.line(20, separatorY, pageWidth - 20, separatorY)
    return separatorY + 8
  }

  renderAppendixSubRecipes(doc, yPosition, appendixRecipes = []) {
    if (!Array.isArray(appendixRecipes) || appendixRecipes.length === 0) {
      return yPosition
    }

    let nextY = this.renderAppendixSectionTitle(doc, yPosition)
    appendixRecipes.forEach(appendixRecipe => {
      nextY = this.renderSingleAppendixRecipe(doc, nextY, appendixRecipe)
    })

    return nextY
  }

  addBrandedFooter(doc, pageNumber) {
    const pageHeight = doc.internal.pageSize.height
    const pageWidth = doc.internal.pageSize.width
    const footerY = pageHeight - 15

    doc.setLineWidth(0.5)
    doc.setDrawColor(200, 200, 200)
    doc.line(20, footerY - 5, pageWidth - 20, footerY - 5)

    doc.setFontSize(10)
    doc.setTextColor(100, 100, 100)
    const currentDate = new Date().toLocaleDateString('en-GB')
    const footerText = `Created on Orders Made Simple @ ${currentDate}`

    const textWidth = doc.getTextWidth(footerText)
    const centerX = (pageWidth - textWidth) / 2
    doc.text(footerText, centerX, footerY)

    if (pageNumber) {
      const pageLabel = `Page ${pageNumber}`
      const pageLabelWidth = doc.getTextWidth(pageLabel)
      doc.text(pageLabel, pageWidth - 20 - pageLabelWidth, footerY)
    }
  }

  // Header carries the recipe name only — page numbers live in the footer.
  addRecipePageHeader(doc, recipeName) {
    const pageWidth = doc.internal.pageSize.width
    const headerY = 10

    doc.setFontSize(10)
    doc.setTextColor(90, 90, 90)

    const recipeLabel = this.truncateToWidth(
      doc,
      `Recipe: ${recipeName || 'Unnamed recipe'}`,
      pageWidth - 40,
    )

    doc.text(recipeLabel, 20, headerY)
  }

  applyRecipePageChrome(doc, { recipeName, startPage, endPage }) {
    const currentPage =
      doc.internal.getCurrentPageInfo?.().pageNumber || endPage
    const totalRecipePages = endPage - startPage + 1

    for (let page = startPage; page <= endPage; page++) {
      const recipePageNumber = page - startPage + 1
      doc.setPage(page)
      this.addRecipePageHeader(doc, recipeName)
      this.addBrandedFooter(doc, `${recipePageNumber}/${totalRecipePages}`)
    }

    doc.setPage(currentPage)
  }

  async loadImageAsBase64(imageUrl) {
    try {
      const response = await fetch(imageUrl)
      const blob = await response.blob()
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })
    } catch (error) {
      console.warn('Failed to load image:', error)
      return ''
    }
  }

  getImageNaturalSize(imageData) {
    return new Promise(resolve => {
      if (!imageData || typeof Image === 'undefined') {
        resolve(null)
        return
      }

      const img = new Image()
      img.onload = () =>
        resolve({
          width: img.naturalWidth || img.width,
          height: img.naturalHeight || img.height,
        })
      img.onerror = () => resolve(null)
      img.src = imageData
    })
  }

  /** Scale an image to fit inside a box without cropping or squashing. */
  fitImageInBox(naturalWidth, naturalHeight, maxWidth, maxHeight) {
    if (
      !Number.isFinite(naturalWidth) ||
      !Number.isFinite(naturalHeight) ||
      naturalWidth <= 0 ||
      naturalHeight <= 0
    ) {
      return { width: maxWidth, height: maxHeight }
    }

    const scale = Math.min(maxWidth / naturalWidth, maxHeight / naturalHeight)
    return {
      width: naturalWidth * scale,
      height: naturalHeight * scale,
    }
  }

  async addFittedImage(
    doc,
    imageData,
    x,
    y,
    maxWidth,
    maxHeight,
    format = 'JPEG',
  ) {
    const naturalSize = await this.getImageNaturalSize(imageData)
    const { width, height } = this.fitImageInBox(
      naturalSize?.width,
      naturalSize?.height,
      maxWidth,
      maxHeight,
    )
    doc.addImage(imageData, format, x, y, width, height)
    return { width, height }
  }

  /**
   * Resolve note images up front. Expired or broken links are common, and a
   * note whose images all fail must not leave a "Notes" heading behind.
   */
  async loadNotesImages(images = []) {
    const loaded = []

    for (const image of images) {
      if (!image?.signedURL) continue

      try {
        const imageData = await this.loadImageAsBase64(image.signedURL)
        if (imageData) loaded.push(imageData)
      } catch (error) {
        console.warn('Could not load note image for PDF:', error)
      }
    }

    return loaded
  }

  async addNotesWithImages(doc, recipe, yPosition) {
    const pageWidth = doc.internal.pageSize.width
    const notesText = (recipe?.notes?.text || '').trim()
    const notesImages = Array.isArray(recipe?.notes?.images)
      ? recipe.notes.images
      : []

    const loadedImages = await this.loadNotesImages(notesImages)

    // Nothing to say: don't leave an orphaned "Notes" heading on the page.
    if (!notesText && loadedImages.length === 0) {
      return yPosition
    }

    const splitNotesText = notesText
      ? doc.splitTextToSize(notesText, pageWidth - 40)
      : null

    // Keep the heading with at least the first few lines of the note.
    yPosition = this.ensureVerticalSpace(
      doc,
      yPosition,
      10 + Math.min(splitNotesText ? splitNotesText.length * 5 : 0, 20),
    )

    doc.setFontSize(14)
    doc.setTextColor(0, 0, 0)
    doc.text('Notes', 20, yPosition)
    yPosition += 10

    if (splitNotesText) {
      doc.setFontSize(11)
      yPosition = this.drawPaginatedLines(doc, splitNotesText, yPosition, 5)
      yPosition += 10
    }

    const MAX_IMAGE_WIDTH = 40
    const MAX_IMAGE_HEIGHT = 40

    for (const imageData of loadedImages) {
      try {
        const naturalSize = await this.getImageNaturalSize(imageData)
        const { width, height } = this.fitImageInBox(
          naturalSize?.width,
          naturalSize?.height,
          MAX_IMAGE_WIDTH,
          MAX_IMAGE_HEIGHT,
        )

        yPosition = this.ensureVerticalSpace(doc, yPosition, height + 10)
        doc.addImage(imageData, 'JPEG', 20, yPosition, width, height)
        yPosition += height + 10
      } catch (error) {
        console.warn('Could not add note image to PDF:', error)
      }
    }

    return yPosition
  }

  addAllergenTextBlock(doc, title, values = [], yPosition) {
    const pageWidth = doc.internal.pageSize.width
    const hasValues = Array.isArray(values) && values.length > 0
    const splitText = hasValues
      ? doc.splitTextToSize(values.join(', '), pageWidth - 40)
      : null

    // Keep the heading with at least the first line of its content.
    const headingHeight = 10
    const bodyHeight = splitText ? splitText.length * 5 + 8 : 10
    yPosition = this.ensureVerticalSpace(
      doc,
      yPosition,
      headingHeight + Math.min(bodyHeight, 15),
    )

    doc.setFontSize(14)
    doc.setTextColor(0, 0, 0)
    doc.text(title, 20, yPosition)
    yPosition += headingHeight

    doc.setFontSize(11)
    if (splitText) {
      yPosition = this.ensureVerticalSpace(doc, yPosition, bodyHeight)
      doc.text(splitText, 20, yPosition)
      yPosition += bodyHeight
    } else {
      doc.text(`No ${title.toLowerCase()} listed`, 20, yPosition)
      yPosition += 10
    }

    return yPosition
  }

  addAllergenMetadata(doc, yPosition, allergenMetadata = {}) {
    const { lastUpdatedAt, lastUpdatedBy } = allergenMetadata
    if (lastUpdatedAt) {
      yPosition = this.ensureVerticalSpace(doc, yPosition, 8)
      doc.setFontSize(10)
      doc.setTextColor(100, 100, 100)
      doc.text(
        `Last updated: ${new Date(lastUpdatedAt).toLocaleDateString(
          'en-GB',
        )} by ${lastUpdatedBy || 'Unknown User'}`,
        20,
        yPosition,
      )
      yPosition += 8
    }
    return yPosition
  }

  addAllergenDisclaimer(doc, yPosition) {
    const pageWidth = doc.internal.pageSize.width
    const disclaimerText =
      'Disclaimer: OMS may receive allergen data from suppliers but cannot guarantee it is accurate, complete, or up to date. OMS accepts no liability for allergen-related incidents. Your kitchen remains responsible for independently verifying allergen information and ensuring customer safety.'

    doc.setFontSize(9)
    doc.setTextColor(90, 90, 90)
    const splitText = doc.splitTextToSize(disclaimerText, pageWidth - 40)
    const neededHeight = splitText.length * 4 + 6

    yPosition = this.ensureVerticalSpace(doc, yPosition, neededHeight)
    doc.text(splitText, 20, yPosition)
    return yPosition + neededHeight
  }

  async renderRecipeHero(doc, recipe, yPosition) {
    doc.setTextColor(0, 0, 0)
    yPosition = this.drawWrappedText(
      doc,
      recipe.name || 'Unnamed recipe',
      yPosition,
      { fontSize: 24, lineHeight: 10, gap: 5 },
    )

    doc.setTextColor(100, 100, 100)
    yPosition = this.drawWrappedText(
      doc,
      `Category: ${recipe?.category || ''}`,
      yPosition,
      { fontSize: 12, lineHeight: 6, gap: 9 },
    )

    if (recipe.image) {
      try {
        const imageData = await this.loadImageAsBase64(recipe.image)
        if (imageData) {
          yPosition = this.ensureVerticalSpace(doc, yPosition, 70)
          const { height } = await this.addFittedImage(
            doc,
            imageData,
            20,
            yPosition,
            60,
            60,
          )
          yPosition += height + 10
        }
      } catch (error) {
        console.warn('Could not add image to PDF:', error)
        yPosition += 10
      }
    } else {
      yPosition += 10
    }

    return yPosition
  }

  renderCostInformation(doc, recipe, yPosition) {
    doc.setFontSize(14)
    doc.setTextColor(0, 0, 0)
    doc.text('Cost Information', 20, yPosition)
    yPosition += 10

    doc.setFontSize(11)
    doc.text(
      `Cost Per Recipe: £${formatMoney(recipe.costPerRecipe)}`,
      20,
      yPosition,
    )
    yPosition += 7
    doc.text(`Portions: ${recipe?.portions || 1}`, 20, yPosition)
    yPosition += 7
    doc.text(
      `Cost Per Portion: £${formatMoney(recipe.costPerPortion)}`,
      20,
      yPosition,
    )
    yPosition += 7
    doc.text(
      `Selling Price (ex VAT): £${formatMoney(recipe.sellingPriceExVat)}`,
      20,
      yPosition,
    )
    yPosition += 7
    doc.text(
      `Selling Price (inc VAT): £${formatMoney(recipe.sellingPriceIncVat)}`,
      20,
      yPosition,
    )
    yPosition += 7
    if (!recipe?.hideGrossProfit) {
      doc.text(`Gross Profit: ${recipe?.grossProfit}`, 20, yPosition)
      yPosition += 7
    }

    // Mirrors the "cost may be out of date" hint on the Recipe Details screen.
    if (recipe?.economicsAreStale) {
      doc.setTextColor(150, 90, 0)
      yPosition = this.drawWrappedText(
        doc,
        `Note: ingredient lines currently total £${formatMoney(
          recipe.lineSum,
        )}, so this saved recipe cost may be out of date.`,
        yPosition,
        { fontSize: 10, lineHeight: 5, gap: 2 },
      )
      doc.setTextColor(0, 0, 0)
    }

    return yPosition + 8
  }

  renderIngredientsTable(doc, recipe, yPosition) {
    doc.setFontSize(14)
    doc.setTextColor(0, 0, 0)
    doc.text('Ingredients', 20, yPosition)
    yPosition += 10

    const ingredients = recipe?.ingredients || []
    doc.autoTable({
      startY: yPosition,
      head: [['SKU', 'Ingredient', 'Quantity', 'UOM / Pack', 'Price (£)']],
      body: this.buildIngredientRows(ingredients),
      foot: [this.buildIngredientTotalRow(ingredients)],
      theme: 'grid',
      headStyles: { fillColor: '#b5d922' },
      footStyles: {
        fillColor: [245, 245, 245],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
      },
      margin: { left: 20, right: 20 },
      styles: { fontSize: 10 },
      columnStyles: {
        0: { cellWidth: 22 },
        2: { cellWidth: 22, halign: 'right' },
        3: { cellWidth: 28 },
        4: { cellWidth: 24, halign: 'right' },
      },
    })

    return doc.lastAutoTable.finalY + 15
  }

  renderSteps(doc, recipe, yPosition) {
    const steps = this.getRecipeStepList(recipe)
    if (steps.length === 0) return yPosition

    // Keep the heading with at least the first step or two.
    yPosition = this.ensureVerticalSpace(
      doc,
      yPosition,
      10 + Math.min(steps.length * 8, 20),
    )

    doc.setFontSize(14)
    doc.setTextColor(0, 0, 0)
    doc.text('Steps', 20, yPosition)
    yPosition += 10

    doc.setFontSize(11)
    return this.drawNumberedList(doc, steps, yPosition) + 5
  }

  /**
   * Shared recipe body used by both single and bulk PDF downloads so the two
   * paths can't drift apart.
   */
  async renderRecipeContent(
    doc,
    recipe,
    {
      appendixSubRecipes = [],
      allergenMetadata = {},
      yPosition = CONTENT_TOP,
    } = {},
  ) {
    yPosition = await this.renderRecipeHero(doc, recipe, yPosition)
    yPosition = this.renderCostInformation(doc, recipe, yPosition)
    yPosition = this.renderIngredientsTable(doc, recipe, yPosition)

    yPosition = this.addAllergenTextBlock(
      doc,
      'Allergens',
      recipe.allergens,
      yPosition,
    )
    yPosition = this.addAllergenTextBlock(
      doc,
      'May contain',
      recipe.allergenTraces,
      yPosition,
    )
    yPosition = this.addAllergenMetadata(doc, yPosition, allergenMetadata)
    yPosition = this.addAllergenDisclaimer(doc, yPosition)

    yPosition = await this.addNotesWithImages(doc, recipe, yPosition)
    yPosition = this.renderSteps(doc, recipe, yPosition)

    if (appendixSubRecipes.length > 0) {
      yPosition = this.ensureVerticalSpace(doc, yPosition, 18)
      yPosition = this.renderAppendixSubRecipes(
        doc,
        yPosition,
        appendixSubRecipes,
      )
    }

    return yPosition
  }

  async generateRecipePDF(
    recipeRaw,
    allRecipes = [],
    allergenMetadata = {},
    venueProductsCatalog = [],
  ) {
    const transformContext = this.buildTransformContext(
      allRecipes,
      venueProductsCatalog,
    )
    const recipe = this.transformRecipe(recipeRaw, transformContext)
    const appendixSubRecipes = this.getAppendixSubRecipes(recipeRaw, allRecipes)
    const doc = new jsPDF()

    await this.renderRecipeContent(doc, recipe, {
      appendixSubRecipes,
      allergenMetadata,
    })

    this.applyRecipePageChrome(doc, {
      recipeName: recipe.name,
      startPage: 1,
      endPage: doc.internal.getNumberOfPages(),
    })

    doc.save(this.buildRecipeFileName(recipe.name))
  }

  async generateAllRecipesPDF(
    recipesRaw = [],
    allergenMetadata = {},
    venueProductsCatalog = [],
  ) {
    const transformContext = this.buildTransformContext(
      recipesRaw,
      venueProductsCatalog,
    )
    const recipes = (recipesRaw || []).map(recipe =>
      this.transformRecipe(recipe, transformContext),
    )
    const doc = new jsPDF()

    for (let i = 0; i < recipes.length; i++) {
      const recipeRaw = recipesRaw[i]
      const recipe = recipes[i]
      const appendixSubRecipes = this.getAppendixSubRecipes(
        recipeRaw,
        recipesRaw,
      )

      if (i > 0) {
        doc.addPage()
      }
      const recipeStartPage = doc.internal.getNumberOfPages()

      await this.renderRecipeContent(doc, recipe, {
        appendixSubRecipes,
        allergenMetadata,
      })

      this.applyRecipePageChrome(doc, {
        recipeName: recipe.name,
        startPage: recipeStartPage,
        endPage: doc.internal.getNumberOfPages(),
      })
    }

    doc.save(`all_recipes_${new Date().toISOString().split('T')[0]}.pdf`)
  }

  async generateMenuPDF(menuData) {
    try {
      const doc = new jsPDF()
      let yPosition = 20

      // Add menu title
      doc.setFont('helvetica', 'bold')
      yPosition = this.drawWrappedText(
        doc,
        menuData.menuName || 'Untitled menu',
        yPosition,
        { fontSize: 20, lineHeight: 9, gap: 6 },
      )

      // Add menu description if available
      if (menuData.description) {
        doc.setFont('helvetica', 'normal')
        yPosition = this.drawWrappedText(doc, menuData.description, yPosition, {
          fontSize: 12,
          lineHeight: 5,
          gap: 10,
        })
      }

      // Group recipes by section
      const recipesBySection = {}
      menuData.recipes.forEach(recipe => {
        const sectionName = recipe.section || 'Main'
        if (!recipesBySection[sectionName]) {
          recipesBySection[sectionName] = []
        }
        recipesBySection[sectionName].push(recipe)
      })

      // Add recipes by section
      Object.keys(recipesBySection).forEach(sectionName => {
        // Check if we need a new page
        if (yPosition > 250) {
          doc.addPage()
          yPosition = 20
        }

        // Add section header
        doc.setFont('helvetica', 'bold')
        yPosition = this.drawWrappedText(doc, sectionName, yPosition, {
          fontSize: 16,
          lineHeight: 8,
          gap: 2,
        })

        // Add recipes in this section
        recipesBySection[sectionName].forEach(recipe => {
          // Check if we need a new page
          if (yPosition > 250) {
            doc.addPage()
            yPosition = 20
          }

          // Add recipe name
          doc.setFont('helvetica', 'bold')
          yPosition = this.drawWrappedText(
            doc,
            recipe.title || 'Unnamed recipe',
            yPosition,
            { fontSize: 14, lineHeight: 7, gap: 1, x: 30 },
          )

          // Add recipe description if available
          if (recipe.description) {
            doc.setFont('helvetica', 'normal')
            yPosition = this.drawWrappedText(
              doc,
              recipe.description,
              yPosition,
              { fontSize: 10, lineHeight: 4, gap: 5, x: 30 },
            )
          }

          // Add price if available
          if (recipe.price) {
            doc.setFontSize(12)
            doc.setFont('helvetica', 'bold')
            doc.text(`£${recipe.price}`, 30, yPosition)
            yPosition += 8
          }

          yPosition += 5
        })

        yPosition += 10
      })

      doc.save(
        `${menuData.menuName
          .replace(/[^a-z0-9]/gi, '_')
          .toLowerCase()}_menu.pdf`,
      )
    } catch (error) {
      console.error('Error generating menu PDF:', error)
      alert('Failed to generate menu PDF')
    }
  }
}

export const pdfGenerator = new PDFGenerator()
